import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import MediaPlaybackSpeedDropdown from './dropdown'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { dispatch } from 'store'
import { IMediaItem } from 'types/media'
import { selectActiveAudio } from 'store/selectors'
import { clampFadeEffectsToDuration } from 'utils/audio'
import { clampDurationToSource } from 'utils/media'

const PlaybackSpeedButton = () => {
  const activeMediaItem = useSelector(
    projectDataSelectors.activeMedia
  ) as IMediaItem | null
  const activeAudioItem = useSelector(selectActiveAudio)

  const activeItemPlaybackSpeed = useMemo(() => {
    if (activeMediaItem) {
      return activeMediaItem.meta.playbackSpeed
    }
    if (activeAudioItem) {
      return activeAudioItem.playbackSpeed
    }
    return 1
  }, [activeMediaItem, activeAudioItem])

  const duration = useMemo(() => {
    if (activeMediaItem) {
      return activeMediaItem.duration
    }
    if (activeAudioItem) {
      return activeAudioItem.duration
    }
    return 0
  }, [activeMediaItem, activeAudioItem])

  const currentSpeed = activeItemPlaybackSpeed ?? 1

  const handleSpeedSelect = useCallback(
    (speed: number) => {
      const baseDuration = duration * (activeItemPlaybackSpeed ?? 1)
      // Changing speed changes duration → the clip must not consume more
      // source than the file has, and a fade may now exceed half the clip.
      if (activeMediaItem) {
        const newDuration = clampDurationToSource({
          duration: baseDuration / speed,
          trimStart: activeMediaItem.meta.trimStart ?? 0,
          originalDuration: activeMediaItem.meta.originalDuration,
          playbackSpeed: speed
        })
        dispatch(
          projectDataThunks.updateMediaItem({
            id: activeMediaItem.id,
            updates: {
              meta: {
                ...activeMediaItem.meta,
                playbackSpeed: speed,
                audioEffects: clampFadeEffectsToDuration(
                  activeMediaItem.meta.audioEffects,
                  newDuration
                )
              },
              duration: newDuration
            }
          })
        )
      }
      if (activeAudioItem) {
        const newDuration = clampDurationToSource({
          duration: baseDuration / speed,
          trimStart: activeAudioItem.mediaStart,
          originalDuration: activeAudioItem.mediaDuration,
          playbackSpeed: speed
        })
        dispatch(
          projectDataThunks.updateAudioItems({
            ids: [activeAudioItem.id],
            updates: {
              playbackSpeed: speed,
              duration: newDuration,
              audioEffects: clampFadeEffectsToDuration(
                activeAudioItem.audioEffects,
                newDuration
              )
            }
          })
        )
      }
    },
    [activeMediaItem, activeAudioItem, activeItemPlaybackSpeed, duration]
  )

  return (
    <MediaPlaybackSpeedDropdown
      initialValue={currentSpeed}
      onClick={handleSpeedSelect}
    />
  )
}

export default memo(PlaybackSpeedButton)
